import {
  createContext,
  useCallback,
  useContext,
  useDeferredValue,
  useMemo,
  useTransition,
} from "react";
import { useQueryParams } from "hooks/useQueryParams";
import { useParams } from "react-router-dom";
import { useWasteFacility } from "./useWasteFacility";

type GraphType =
  | "hazardousWaste"
  | "hazardousWasteByMonth"
  | "highestGeneratingWasteStreams";

type Filter = {
  graphType: GraphType;
  year: string;
};

export type WasteFacilityState = {
  immediateFilters: Partial<Filter>;
  deferredFilters: Partial<Filter>;
  isPending: boolean;
};

type WasteFacilityContextType = {
  wasteFacilityState: WasteFacilityState;
  setWasteFacilityState: (cb: (state: Partial<Filter>) => void) => void;
  /** The facility ID to use for waste-related queries (primary facility if colocated) */
  effectiveWasteFacilityId: string;
  /** The primary waste facility info if this facility is colocated, null otherwise */
  colocationWasteFacility: { id: string; name: string } | null | undefined;
};

const WasteFacilityStateContext = createContext<
  WasteFacilityContextType | undefined
>(undefined);

export function useWasteFacilityState() {
  const context = useContext(WasteFacilityStateContext);
  if (!context) {
    throw new Error(
      "useWasteFacilityState must be used within a WasteFacilityStateProvider"
    );
  }
  return context;
}

export const WasteFacilityProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { facilityId } = useParams<{ facilityId: string }>();
  const { data } = useWasteFacility(facilityId);
  const { filters, setFilters } = useQueryParams<Filter>();

  const deferredFilters = useDeferredValue(filters);

  const [isPending, startTransition] = useTransition();

  const wasteFacilityState = useMemo(
    () => ({ deferredFilters, immediateFilters: filters, isPending }),
    [deferredFilters, filters, isPending]
  );

  const setWasteFacilityState = useCallback(
    (cb: (state: Partial<Filter>) => void) => {
      startTransition(() => {
        setFilters((f) => {
          cb(f);
        });
      });
    },
    [setFilters]
  );

  // Use the primary facility ID for waste queries if this facility is colocated
  const effectiveWasteFacilityId =
    data.facility?.colocationWasteFacilityId ?? facilityId ?? "";

  const colocationWasteFacility = data.facility?.colocationWasteFacility;

  const value = useMemo(
    () => ({
      wasteFacilityState,
      setWasteFacilityState,
      effectiveWasteFacilityId,
      colocationWasteFacility,
    }),
    [
      wasteFacilityState,
      setWasteFacilityState,
      effectiveWasteFacilityId,
      colocationWasteFacility,
    ]
  );

  return (
    <WasteFacilityStateContext.Provider value={value}>
      {children}
    </WasteFacilityStateContext.Provider>
  );
};
